import { ChevronDown, Check } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { twMerge } from 'tailwind-merge';
import { useAppStore } from '@/core/store/app.store';
import { toIso2 } from '@/core/i18n/country-code';
import type { ICountry } from '@/domain/core';

interface CountrySwitcherProps {
  is_collapsed?: boolean;
}

const COUNTRY_NAMES: Record<string, string> = {
  MEX: 'México',
  COL: 'Colombia',
  PER: 'Perú',
  CHL: 'Chile',
  ARG: 'Argentina',
};

/**
 * Inline SVG flags to avoid adding npm dependencies and breaking pnpm-lock.yaml
 */
function Flag({ iso2, className }: { iso2: string; className?: string }) {
  const code = iso2.toUpperCase();

  if (code === 'MX') {
    return (
      <svg className={className} viewBox="0 0 32 20" xmlns="http://www.w3.org/2000/svg">
        <rect fill="#006847" width="10.6" height="20"/>
        <rect fill="#FFF" x="10.6" width="10.8" height="20"/>
        <rect fill="#CE1126" x="21.4" width="10.6" height="20"/>
      </svg>
    );
  }

  if (code === 'CO') {
    return (
      <svg className={className} viewBox="0 0 3 2" xmlns="http://www.w3.org/2000/svg">
        <rect fill="#FCD116" width="3" height="1"/>
        <rect fill="#003893" y="1" width="3" height=".5"/>
        <rect fill="#CE1126" y="1.5" width="3" height=".5"/>
      </svg>
    );
  }

  if (code === 'PE') {
    return (
      <svg className={className} viewBox="0 0 3 2" xmlns="http://www.w3.org/2000/svg">
        <rect fill="#D91023" width="1" height="2"/>
        <rect fill="#FFF" x="1" width="1" height="2"/>
        <rect fill="#D91023" x="2" width="1" height="2"/>
      </svg>
    );
  }

  if (code === 'CL') {
    return (
      <svg className={className} viewBox="0 0 3 2" xmlns="http://www.w3.org/2000/svg">
        <rect fill="#FFF" width="3" height="1"/>
        <rect fill="#D52B1E" y="1" width="3" height="1"/>
        <rect fill="#0039A6" width="1" height="1"/>
        <polygon fill="#FFF" points="0.5,0.2 0.59,0.48 0.88,0.48 0.64,0.66 0.73,0.94 0.5,0.76 0.27,0.94 0.36,0.66 0.12,0.48 0.41,0.48"/>
      </svg>
    );
  }

  if (code === 'AR') {
    return (
      <svg className={className} viewBox="0 0 3 2" xmlns="http://www.w3.org/2000/svg">
        <rect fill="#74ACDF" width="3" height="2"/>
        <rect fill="#FFF" y="0.66" width="3" height="0.66"/>
        <circle fill="#F6B40E" cx="1.5" cy="1" r="0.2"/>
      </svg>
    );
  }

  return null;
}

export function CountrySwitcher(props: CountrySwitcherProps) {
  const { is_collapsed } = props;
  const queryClient = useQueryClient();
  const country = useAppStore((state) => state.country);
  const allowed_countries = useAppStore((state) => state.allowed_countries);
  const setCountry = useAppStore((state) => state.setCountry);

  const activeIso2 = toIso2(country);

  const onSwitch = (newCountry: ICountry) => {
    if (newCountry === country) return;
    setCountry(newCountry);
    queryClient.invalidateQueries();
  };

  if (allowed_countries.length <= 1) {
    return (
      <div className={twMerge(
        "flex items-center gap-3 px-4 py-3 border-b border-base-300",
        is_collapsed && "justify-center px-0"
      )}>
        <div className="w-6 h-4 overflow-hidden rounded-sm shadow-sm shrink-0 border border-base-content/10">
          <Flag iso2={activeIso2} className="w-full h-full object-cover" />
        </div>
        {!is_collapsed && (
          <span className="text-sm font-semibold truncate">{COUNTRY_NAMES[country]}</span>
        )}
      </div>
    );
  }

  return (
    <div className="dropdown dropdown-bottom w-full border-b border-base-300">
      <div
        tabIndex={0}
        role="button"
        className={twMerge(
          "flex items-center gap-3 w-full px-4 py-4 hover:bg-base-200 transition-colors group",
          is_collapsed && "justify-center px-0"
        )}
      >
        <div className="w-7 h-5 overflow-hidden rounded-sm shadow-sm shrink-0 border border-base-content/10">
          <Flag iso2={activeIso2} className="w-full h-full object-cover" />
        </div>

        {!is_collapsed && (
          <>
            <div className="flex flex-1 flex-col items-start min-w-0">
              <span className="text-[10px] uppercase font-bold text-base-content/40 leading-none mb-1">
                País
              </span>
              <span className="text-sm font-bold truncate leading-none">
                {COUNTRY_NAMES[country]}
              </span>
            </div>
            <ChevronDown className="w-4 h-4 opacity-30 group-hover:opacity-100 transition-opacity" />
          </>
        )}
      </div>

      <ul
        tabIndex={0}
        className="dropdown-content z-[100] menu p-1 shadow-2xl bg-base-100 border border-base-300 rounded-box w-52 mt-1 ml-2"
      >
        <li className="menu-title px-3 py-2 text-[10px] uppercase font-bold text-base-content/40">
          Cambiar contexto
        </li>
        {allowed_countries.map((c) => {
          const is_active = c === country;

          return (
            <li key={c}>
              <button
                onClick={() => onSwitch(c)}
                className={twMerge(
                  "flex items-center gap-3 py-3",
                  is_active && "bg-primary/10 text-primary font-semibold"
                )}
              >
                <div className="w-5 h-3.5 overflow-hidden rounded-sm shadow-xs shrink-0 border border-base-content/10">
                  <Flag iso2={toIso2(c)} className="w-full h-full object-cover" />
                </div>
                <span className="flex-1 text-sm">{COUNTRY_NAMES[c]}</span>
                {is_active && <Check className="w-4 h-4 shrink-0" />}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
